import React, { useState, useEffect } from 'react';
import { useNavigate, useParams, useSearchParams, useLocation } from 'react-router-dom';
import axios from 'axios';
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import ImageWithWrap from '../../components/editor/ImageWithWrap';
import { addImageFromUrl, createImagePasteHandler } from '../../components/editor/editorImageAlt';
import { EditorImageAltControls } from '../../components/editor/EditorImageAltControls';
import Link from '@tiptap/extension-link';
import Underline from '@tiptap/extension-underline';
import TextAlign from '@tiptap/extension-text-align';
import Placeholder from '@tiptap/extension-placeholder';
import { Table, TableRow, TableHeader, TableCell } from '@tiptap/extension-table';
import {
  ArrowLeft,
  Loader2,
  Bold,
  Italic,
  Underline as UnderlineIcon,
  Strikethrough,
  List,
  ListOrdered,
  Quote,
  Code,
  FileCode,
  Image as ImageIcon,
  Link as LinkIcon,
  Heading1,
  Heading2,
  Heading3,
  AlignLeft,
  AlignCenter,
  AlignRight,
  AlignJustify,
  WrapText,
  Square,
  Undo,
  Redo,
  Minus,
  Table as TableIcon,
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Switch } from '../../components/ui/switch';
import { toast } from 'sonner';
import { validateJotformInput } from '../../utils/jotformEmbed';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

function rewriteUploadUrlsInHtml(html) {
  if (!html || typeof html !== 'string') return html;
  const base = (BACKEND_URL || '').trim().replace(/\/+$/, '');
  if (!base) return html;
  return html.replace(/\ssrc=(["'])\/uploads\//g, ` src=$1${base}/uploads/`);
}

function newsletterDoc(html) {
  const rewritten = rewriteUploadUrlsInHtml(html || '');
  const raw = rewritten.trim();
  const isFullDocument = /^\s*<!DOCTYPE\s+html\s*>/i.test(raw) || /^\s*<html[\s>]/i.test(raw);
  if (isFullDocument) {
    return raw;
  }
  const safe = (rewritten || '')
    .replace(/<\/body\>/gi, '&lt;/body&gt;')
    .replace(/<\/html\>/gi, '&lt;/html&gt;');
  return `<!DOCTYPE html><html><head><meta charset="utf-8"><meta name="viewport" content="width=device-width, initial-scale=1"><style>body{margin:0;padding:0;}img{max-width:100%;height:auto;}table{max-width:100%;}</style></head><body>${safe}</body></html>`;
}

const editorStyles = `
  .ProseMirror { min-height: 320px; padding: 1rem; outline: none; }
  .ProseMirror > * + * { margin-top: 0.75em; }
  .ProseMirror h1 { font-size: 2em; font-weight: 700; }
  .ProseMirror h2 { font-size: 1.5em; font-weight: 600; }
  .ProseMirror h3 { font-size: 1.25em; font-weight: 600; }
  .ProseMirror p { margin-bottom: 0.75em; line-height: 1.6; }
  .ProseMirror ul { list-style-type: disc; padding-left: 1.5em; }
  .ProseMirror ol { list-style-type: decimal; padding-left: 1.5em; }
  .ProseMirror blockquote { border-left: 4px solid #C8473A; padding-left: 1em; margin: 1em 0; font-style: italic; color: #555; }
  .ProseMirror code { background: #f4f4f4; padding: 0.2em 0.4em; border-radius: 4px; font-size: 0.9em; }
  .ProseMirror pre { background: #1e1e1e; color: #d4d4d4; padding: 1em; border-radius: 8px; margin: 1em 0; }
  .ProseMirror img { max-width: 100%; height: auto; border-radius: 8px; }
  .ProseMirror p[style*="text-align: center"] img { display: block; margin-left: auto; margin-right: auto; }
  .ProseMirror p[style*="text-align: right"] img { display: block; margin-left: auto; }
  .ProseMirror [style*="text-align: justify"] { text-align: justify; }
  .ProseMirror img[data-float="left"] { float: left; margin: 0 1em 0.5em 0; max-width: 50%; }
  .ProseMirror img[data-float="right"] { float: right; margin: 0 0 0.5em 1em; max-width: 50%; }
  .ProseMirror img[data-float="block"],
  .ProseMirror img:not([data-float]) { float: none; display: block; }
  .ProseMirror a { color: #C8473A; text-decoration: underline; }
  .ProseMirror hr { border: none; border-top: 2px solid #e5e5e5; margin: 1.5em 0; }
  .ProseMirror table { border-collapse: collapse; width: 100%; margin: 1em 0; }
  .ProseMirror th, .ProseMirror td { border: 1px solid #e5e7eb; padding: 0.5em 0.75em; text-align: left; }
  .ProseMirror th { background: #f9fafb; font-weight: 600; }
  .ProseMirror p.is-editor-empty:first-child::before { content: attr(data-placeholder); float: left; color: #adb5bd; height: 0; }
  .ProseMirror .is-editor-empty::before { content: attr(data-placeholder); color: #adb5bd; float: left; height: 0; }
`;

const ToolbarButton = ({ onClick, isActive, disabled, children, title }) => (
  <Button type="button" variant={isActive ? 'secondary' : 'ghost'} size="sm" onClick={onClick} disabled={disabled} title={title} className="h-8 w-8 p-0">
    {children}
  </Button>
);
const ToolbarDivider = () => <div className="w-px h-6 bg-gray-200 mx-1" />;

const MenuBar = ({ editor, onUploadImageFromUrl, onPasteHtml }) => {
  if (!editor) return null;
  const addImage = () => addImageFromUrl(editor, onUploadImageFromUrl);
  const addLink = () => {
    const prev = editor.getAttributes('link').href;
    const url = window.prompt('Enter URL:', prev);
    if (url === null) return;
    if (url === '') editor.chain().focus().extendMarkRange('link').unsetLink().run();
    else editor.chain().focus().extendMarkRange('link').setLink({ href: url }).run();
  };
  return (
    <div className="border-b bg-gray-50/50 p-2 flex flex-wrap items-center gap-0.5">
      <ToolbarButton onClick={() => editor.chain().focus().undo().run()} disabled={!editor.can().undo()} title="Undo"><Undo className="w-4 h-4" /></ToolbarButton>
      <ToolbarButton onClick={() => editor.chain().focus().redo().run()} disabled={!editor.can().redo()} title="Redo"><Redo className="w-4 h-4" /></ToolbarButton>
      <ToolbarDivider />
      <ToolbarButton onClick={() => editor.chain().focus().toggleHeading({ level: 1 }).run()} isActive={editor.isActive('heading', { level: 1 })} title="H1"><Heading1 className="w-4 h-4" /></ToolbarButton>
      <ToolbarButton onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()} isActive={editor.isActive('heading', { level: 2 })} title="H2"><Heading2 className="w-4 h-4" /></ToolbarButton>
      <ToolbarButton onClick={() => editor.chain().focus().toggleHeading({ level: 3 }).run()} isActive={editor.isActive('heading', { level: 3 })} title="H3"><Heading3 className="w-4 h-4" /></ToolbarButton>
      <ToolbarDivider />
      <ToolbarButton onClick={() => editor.chain().focus().toggleBold().run()} isActive={editor.isActive('bold')} title="Bold"><Bold className="w-4 h-4" /></ToolbarButton>
      <ToolbarButton onClick={() => editor.chain().focus().toggleItalic().run()} isActive={editor.isActive('italic')} title="Italic"><Italic className="w-4 h-4" /></ToolbarButton>
      <ToolbarButton onClick={() => editor.chain().focus().toggleUnderline().run()} isActive={editor.isActive('underline')} title="Underline"><UnderlineIcon className="w-4 h-4" /></ToolbarButton>
      <ToolbarButton onClick={() => editor.chain().focus().toggleStrike().run()} isActive={editor.isActive('strike')} title="Strike"><Strikethrough className="w-4 h-4" /></ToolbarButton>
      <ToolbarDivider />
      <ToolbarButton onClick={() => editor.chain().focus().setTextAlign('left').run()} isActive={editor.isActive({ textAlign: 'left' })} title="Align left"><AlignLeft className="w-4 h-4" /></ToolbarButton>
      <ToolbarButton onClick={() => editor.chain().focus().setTextAlign('center').run()} isActive={editor.isActive({ textAlign: 'center' })} title="Align center"><AlignCenter className="w-4 h-4" /></ToolbarButton>
      <ToolbarButton onClick={() => editor.chain().focus().setTextAlign('right').run()} isActive={editor.isActive({ textAlign: 'right' })} title="Align right"><AlignRight className="w-4 h-4" /></ToolbarButton>
      <ToolbarButton onClick={() => editor.chain().focus().setTextAlign('justify').run()} isActive={editor.isActive({ textAlign: 'justify' })} title="Justify (text wrap)"><AlignJustify className="w-4 h-4" /></ToolbarButton>
      <ToolbarDivider />
      <ToolbarButton onClick={() => editor.chain().focus().toggleBulletList().run()} isActive={editor.isActive('bulletList')} title="Bullet list"><List className="w-4 h-4" /></ToolbarButton>
      <ToolbarButton onClick={() => editor.chain().focus().toggleOrderedList().run()} isActive={editor.isActive('orderedList')} title="Numbered list"><ListOrdered className="w-4 h-4" /></ToolbarButton>
      <ToolbarDivider />
      <ToolbarButton onClick={() => editor.chain().focus().toggleBlockquote().run()} isActive={editor.isActive('blockquote')} title="Quote"><Quote className="w-4 h-4" /></ToolbarButton>
      <ToolbarButton onClick={() => editor.chain().focus().toggleCodeBlock().run()} isActive={editor.isActive('codeBlock')} title="Code"><Code className="w-4 h-4" /></ToolbarButton>
      <ToolbarButton onClick={() => editor.chain().focus().setHorizontalRule().run()} title="Rule"><Minus className="w-4 h-4" /></ToolbarButton>
      <ToolbarButton onClick={() => editor.chain().focus().insertTable({ rows: 3, cols: 3, withHeaderRow: true }).run()} title="Insert table"><TableIcon className="w-4 h-4" /></ToolbarButton>
      <ToolbarDivider />
      <ToolbarButton onClick={addImage} title="Image (stored locally)"><ImageIcon className="w-4 h-4" /></ToolbarButton>
      <ToolbarButton onClick={addLink} isActive={editor.isActive('link')} title="Link"><LinkIcon className="w-4 h-4" /></ToolbarButton>
      <ToolbarDivider />
      <ToolbarButton onClick={() => editor.chain().focus().updateAttributes('image', { float: 'left' }).run()} isActive={editor.isActive('image') && editor.getAttributes('image').float === 'left'} title="Wrap text (image left)"><WrapText className="w-4 h-4" /></ToolbarButton>
      <ToolbarButton onClick={() => editor.chain().focus().updateAttributes('image', { float: 'right' }).run()} isActive={editor.isActive('image') && editor.getAttributes('image').float === 'right'} title="Wrap text (image right)"><WrapText className="w-4 h-4 rotate-180" /></ToolbarButton>
      <ToolbarButton onClick={() => editor.chain().focus().updateAttributes('image', { float: null }).run()} isActive={editor.isActive('image') && !editor.getAttributes('image').float} title="No wrap (block)"><Square className="w-4 h-4" /></ToolbarButton>
      <EditorImageAltControls editor={editor} />
      {onPasteHtml && (
        <>
          <ToolbarDivider />
          <ToolbarButton onClick={onPasteHtml} title="Paste HTML"><FileCode className="w-4 h-4" /></ToolbarButton>
        </>
      )}
    </div>
  );
};

export default function ResourceForm() {
  const navigate = useNavigate();
  const { id } = useParams();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const isNew = !id;
  const typeFromQuery = searchParams.get('type');
  const typeFromPathname = location.pathname.includes('newsletters')
    ? 'newsletter'
    : location.pathname.includes('representations')
      ? 'representation'
      : location.pathname.includes('regulatory-updates')
        ? 'regulatory'
        : null;
  const resourceTypeValue = typeFromPathname || typeFromQuery || 'newsletter';

  const isNewsletter = resourceTypeValue === 'newsletter';
  const isRepresentation = resourceTypeValue === 'representation';
  const isRegulatory = resourceTypeValue === 'regulatory';
  const editorRef = React.useRef(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [localizingImages, setLocalizingImages] = useState(false);
  const [contentMode, setContentMode] = useState(() => (resourceTypeValue === 'newsletter' ? 'html' : 'editor'));
  const [formData, setFormData] = useState({
    title: '',
    content: '',
    summary: '',
    link_url: '',
    cover_image_url: '',
    published_date: new Date().toISOString().split('T')[0],
    is_featured: false,
    jotform_form_id: '',
  });

  const uploadBase = isNewsletter
    ? `${API}/admin/newsletters`
    : isRepresentation
      ? `${API}/admin/representations`
      : `${API}/admin/regulatory-updates`;
  const uploadResourceImageFile = async (file) => {
    const fd = new FormData();
    fd.append('file', file);
    const { data } = await axios.post(`${uploadBase}/upload-image`, fd, { withCredentials: true, headers: { 'Content-Type': 'multipart/form-data' } });
    const url = data.url;
    return url.startsWith('http') ? url : `${BACKEND_URL}${url}`;
  };
  const uploadResourceImageFromUrl = async (imageUrl) => {
    const { data } = await axios.post(`${uploadBase}/upload-image-from-url`, { url: imageUrl }, { withCredentials: true });
    const url = data.url;
    return url.startsWith('http') ? url : `${BACKEND_URL}${url}`;
  };

  const uploadCoverImageFile = async (file) => {
    const fd = new FormData();
    fd.append('file', file);
    const { data } = await axios.post(`${API}/admin/representations/upload-cover`, fd, {
      withCredentials: true,
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    const url = data.url;
    return url.startsWith('http') ? url : `${BACKEND_URL}${url}`;
  };

  const handlePasteHtml = () => {
    const html = window.prompt('Paste HTML content:');
    if (html != null && html.trim() && editorRef.current) {
      editorRef.current.commands.setContent(html.trim());
    }
  };

  const handleSwitchToEditor = () => {
    if (isNewsletter) return;
    setContentMode('editor');
    const ed = editorRef.current;
    if (ed) ed.commands.setContent((formData.content || '').trim() || '');
  };

  const handleSwitchToHtml = () => {
    setContentMode('html');
    const ed = editorRef.current;
    if (ed) {
      const html = ed.getHTML();
      setFormData((prev) => ({ ...prev, content: html || prev.content }));
    }
  };

  const handleLocalizeImages = async () => {
    if (!id || !isNewsletter) return;
    setLocalizingImages(true);
    try {
      const { data } = await axios.post(
        `${API}/admin/newsletters/${id}/localize-images`,
        {},
        { withCredentials: true }
      );
      const count = data.localized_count ?? 0;
      if (count > 0) {
        const newContent = data.content ?? '';
        setFormData((prev) => ({ ...prev, content: newContent }));
        if (editorRef.current && !isNewsletter) editorRef.current.commands.setContent(newContent || '');
        toast.success(`${count} image${count === 1 ? '' : 's'} localized. Content updated.`);
      } else {
        toast.info('No external images to localize.');
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to localize images');
    } finally {
      setLocalizingImages(false);
    }
  };

  const editor = useEditor({
    extensions: [
      StarterKit.configure({ heading: { levels: [1, 2, 3] } }),
      ImageWithWrap.configure({ HTMLAttributes: { class: 'rounded-lg max-w-full' } }),
      Link.configure({ openOnClick: false, HTMLAttributes: { class: 'text-[#C8473A] underline' } }),
      Underline,
      TextAlign.configure({ types: ['heading', 'paragraph'] }),
      Table.configure({ resizable: true }),
      TableRow,
      TableHeader,
      TableCell,
      Placeholder.configure({ placeholder: 'Write content here...' }),
    ],
    content: '',
    editorProps: {
      handlePaste: createImagePasteHandler(uploadResourceImageFile, () => editorRef.current),
    },
    onUpdate: ({ editor: e }) => setFormData((prev) => ({ ...prev, content: e.getHTML() })),
  });

  React.useEffect(() => {
    editorRef.current = editor;
  }, [editor]);

  useEffect(() => {
    if (!isNew && id) {
      setLoading(true);
      const url = isNewsletter
        ? `${API}/admin/newsletters/${id}`
        : isRepresentation
          ? `${API}/admin/representations/${id}`
          : `${API}/admin/regulatory-updates/${id}`;
      axios
        .get(url, { withCredentials: true })
        .then((res) => {
          const d = res.data;
          const content = d.content || '';
          setFormData((prev) => ({
            ...prev,
            title: d.title || '',
            content,
            summary: d.summary || '',
            link_url: d.link_url || '',
            cover_image_url: d.cover_image_url || '',
            published_date: d.published_date ? d.published_date.split('T')[0] : prev.published_date,
            is_featured: !!d.is_featured,
            jotform_form_id: d.jotform_form_id || '',
          }));
          if (editor && content && !isNewsletter) editor.commands.setContent(content);
        })
        .catch(() => toast.error('Failed to load'))
        .finally(() => setLoading(false));
    }
  }, [editor, id, isNew, isNewsletter, isRepresentation]);

  useEffect(() => {
    if (!editor || !formData.content || !editor.isEmpty || isNewsletter) return;
    editor.commands.setContent(formData.content);
  }, [editor, formData.content, isNewsletter]);

  const handleChange = (field, value) => setFormData((prev) => ({ ...prev, [field]: value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.title || !formData.content) {
      toast.error('Title and content are required');
      return;
    }
    let jotformFormId = '';
    if (isNewsletter && formData.jotform_form_id.trim()) {
      const result = validateJotformInput(formData.jotform_form_id);
      if (!result.valid) {
        toast.error(result.error);
        return;
      }
      jotformFormId = result.formId;
    }
    setSaving(true);
    try {
      const payload = {
        title: formData.title,
        content: formData.content,
        published_date: new Date(formData.published_date).toISOString(),
      };
      if (isNewsletter) {
        payload.is_featured = formData.is_featured;
        payload.jotform_form_id = jotformFormId; // '' removes the form
        if (isNew) {
          await axios.post(`${API}/admin/newsletters`, payload, { withCredentials: true });
        } else {
          await axios.put(`${API}/admin/newsletters/${id}`, payload, { withCredentials: true });
        }
      } else if (isRepresentation) {
        payload.summary = formData.summary || '';
        payload.cover_image_url = formData.cover_image_url?.trim() || null;
        if (isNew) {
          await axios.post(`${API}/admin/representations`, payload, { withCredentials: true });
        } else {
          await axios.put(`${API}/admin/representations/${id}`, payload, { withCredentials: true });
        }
      } else {
        payload.summary = formData.summary || '';
        payload.link_url = formData.link_url || null;
        if (isNew) {
          await axios.post(`${API}/admin/regulatory-updates`, payload, { withCredentials: true });
        } else {
          await axios.put(`${API}/admin/regulatory-updates/${id}`, payload, { withCredentials: true });
        }
      }
      toast.success(isNew ? 'Created' : 'Updated');
      navigate('/admin/resources');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed');
    } finally {
      setSaving(false);
    }
  };

  const backUrl = '/admin/resources';
  const typeLabel = isNewsletter ? 'Newsletter' : isRepresentation ? 'Representation' : 'Regulatory Update';
  const title = isNew ? `Add ${typeLabel}` : `Edit ${typeLabel}`;

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-8 h-8 animate-spin text-[#C8473A]" />
      </div>
    );
  }

  return (
    <div>
      <style>{editorStyles}</style>
      <div className="flex items-center gap-4 mb-6">
        <Button variant="ghost" onClick={() => navigate(backUrl)}>
          <ArrowLeft className="w-4 h-4" />
        </Button>
        <h1 className="text-2xl font-bold">{title}</h1>
      </div>

      <form onSubmit={handleSubmit} className="max-w-3xl bg-white rounded-lg border p-6 space-y-6">
        {isNew && (
          <div>
            <Label>Type</Label>
            <Select
              value={resourceTypeValue}
              onValueChange={(v) => {
                navigate(`/admin/resources/new?type=${v}`, { replace: true });
                setContentMode(v === 'newsletter' ? 'html' : 'editor');
                setFormData((prev) => ({
                  ...prev,
                  is_featured: false,
                  cover_image_url: v === 'representation' ? prev.cover_image_url : '',
                }));
              }}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="newsletter">Newsletter</SelectItem>
                <SelectItem value="regulatory">Regulatory Update</SelectItem>
                <SelectItem value="representation">Representation</SelectItem>
              </SelectContent>
            </Select>
          </div>
        )}

        <div>
          <Label>Title *</Label>
          <Input value={formData.title} onChange={(e) => handleChange('title', e.target.value)} required />
        </div>

        <div>
          <Label>Content *</Label>
          <div className="mt-2 space-y-3">
            {isNewsletter && (
              <div className="space-y-2">
                <p className="text-xs text-gray-500">
                  Newsletters use Mailchimp/HTML. Paste your template below; layout is preserved as-is.
                </p>
                {!isNew && (
                  <div>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={handleLocalizeImages}
                      disabled={localizingImages}
                    >
                      {localizingImages ? (
                        <>
                          <Loader2 className="w-4 h-4 animate-spin mr-1.5" />
                          Localizing…
                        </>
                      ) : (
                        'Re-localize images'
                      )}
                    </Button>
                    <span className="ml-2 text-xs text-gray-500">Download external images and save locally so links survive domain move.</span>
                  </div>
                )}
              </div>
            )}

            {isNewsletter ? (
              <div className="space-y-3">
                <Textarea
                  value={formData.content}
                  onChange={(e) => handleChange('content', e.target.value)}
                  rows={18}
                  className="font-mono text-xs"
                  placeholder="Paste Mailchimp/WordPress HTML here..."
                />
                <div className="rounded-lg border bg-white p-4">
                  <div className="text-xs font-medium text-gray-500 mb-2">Preview</div>
                  <iframe
                    srcDoc={newsletterDoc(formData.content || '')}
                    title="Newsletter preview"
                    className="w-full min-h-[400px] border border-gray-200 rounded-lg bg-white"
                    sandbox="allow-same-origin"
                  />
                </div>
              </div>
            ) : (
              <div className="border rounded-lg overflow-hidden bg-white">
                <MenuBar
                  editor={editor}
                  onUploadImageFromUrl={uploadResourceImageFromUrl}
                  onPasteHtml={isNewsletter ? handlePasteHtml : undefined}
                />
                <EditorContent editor={editor} className="min-h-[320px]" />
              </div>
            )}
          </div>
        </div>

        {(isRegulatory || isRepresentation) && (
          <div>
            <Label>Summary (optional, for list preview)</Label>
            <Textarea
              value={formData.summary}
              onChange={(e) => handleChange('summary', e.target.value)}
              rows={2}
              placeholder="Short plain-text summary"
            />
          </div>
        )}

        {isRegulatory && (
          <div>
            <Label>Link URL (optional)</Label>
            <Input
              value={formData.link_url}
              onChange={(e) => handleChange('link_url', e.target.value)}
              type="url"
              placeholder="https://..."
            />
          </div>
        )}

        {isRepresentation && (
          <div>
            <Label>Cover image (optional)</Label>
            <p className="text-xs text-gray-500 mb-2">Shown on the Resources page card. Body images use the editor toolbar.</p>
            {formData.cover_image_url ? (
              <div className="space-y-2">
                <img
                  src={
                    formData.cover_image_url.startsWith('http')
                      ? formData.cover_image_url
                      : `${BACKEND_URL}${formData.cover_image_url}`
                  }
                  alt="Cover preview"
                  className="max-h-40 rounded-lg border object-cover"
                />
                <Button type="button" variant="outline" size="sm" onClick={() => handleChange('cover_image_url', '')}>
                  Remove cover
                </Button>
              </div>
            ) : null}
            <Input
              type="file"
              accept="image/*"
              className="mt-2"
              onChange={async (e) => {
                const file = e.target.files?.[0];
                e.target.value = '';
                if (!file) return;
                try {
                  const url = await uploadCoverImageFile(file);
                  handleChange('cover_image_url', url);
                  toast.success('Cover uploaded');
                } catch (err) {
                  toast.error(err.response?.data?.detail || 'Upload failed');
                }
              }}
            />
          </div>
        )}

        {isNewsletter && (
          <div>
            <Label>Jotform form (optional)</Label>
            <p className="text-xs text-gray-500 mb-2">
              Shown below the newsletter content. Enter a Jotform form ID, form URL, or paste the embed script tag. Leave blank for no form.
            </p>
            <Input
              value={formData.jotform_form_id}
              onChange={(e) => handleChange('jotform_form_id', e.target.value)}
              placeholder="e.g. 201753044520444 or https://form.jotform.com/201753044520444"
            />
          </div>
        )}

        {isNewsletter && (
          <div className="flex items-center gap-3">
            <Switch checked={formData.is_featured} onCheckedChange={(v) => handleChange('is_featured', v)} />
            <Label>Featured (show as hero on Resources page)</Label>
          </div>
        )}

        <div>
          <Label>Published Date</Label>
          <Input
            type="date"
            value={formData.published_date}
            onChange={(e) => handleChange('published_date', e.target.value)}
          />
        </div>

        <div className="flex justify-end gap-4">
          <Button type="button" variant="outline" onClick={() => navigate(backUrl)}>
            Cancel
          </Button>
          <Button type="submit" className="bg-[#C8473A] hover:bg-[#B74F40]" disabled={saving}>
            {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
            {isNew ? 'Create' : 'Update'}
          </Button>
        </div>
      </form>
    </div>
  );
}
